/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, MenuItem, Typography } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  FileMetaText,
  MappingBody,
  MappingCell,
  MappingColumnName,
  MappingHeaderCell,
  MappingHeaderRow,
  MappingMetaRow,
  MappingRow,
  MappingSelect,
  MappingTable,
  MetaChip,
  SampleText,
} from "./styles.jsx";

const renderOptionLabel = (option) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#334155" }}>
      {option.label}
    </Typography>
    {option.required ? (
      <Typography component="span" sx={{ color: "#dc2626", fontWeight: 700 }}>
        *
      </Typography>
    ) : null}
  </Box>
);

export default function ColumnMappingStep({
  fileName,
  rowCount,
  columnCount,
  headers,
  mappings,
  onMappingChange,
  fieldOptions,
  sampleValues,
}) {
  return (
    <div data-testid="manual-csv-import-mapping-step">
      <MappingMetaRow>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <InsertDriveFileOutlinedIcon sx={{ color: "#94a3b8" }} />
            <FileMetaText sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fileName}
            </FileMetaText>
          </Box>
          <MetaChip label={`${rowCount} rows`} />
          <MetaChip label={`${columnCount} columns`} />
        </Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#64748b", whiteSpace: "nowrap" }}>
          Auto-Mapping attempted - adjust as needed
        </Typography>
      </MappingMetaRow>

      <MappingTable>
        <MappingHeaderRow>
          <MappingHeaderCell>CSV Column</MappingHeaderCell>
          <MappingHeaderCell>Map to system field</MappingHeaderCell>
          <MappingHeaderCell>Sample values</MappingHeaderCell>
        </MappingHeaderRow>

        <MappingBody>
          {headers.map((header) => {
            const currentValue = mappings[header.normalizedHeader] || "ignore";
            const selectedValues = Object.entries(mappings)
              .filter(([key, value]) => key !== header.normalizedHeader && value && value !== "ignore")
              .map(([, value]) => value);
            const isIgnored = currentValue === "ignore";

            return (
              <MappingRow key={header.normalizedHeader} disabledrow={isIgnored ? 1 : 0} data-testid={`manual-csv-mapping-row-${header.normalizedHeader}`}>
                <MappingCell>
                  <MappingColumnName>{header.originalHeader}</MappingColumnName>
                </MappingCell>

                <MappingCell>
                  <MappingSelect
                    data-testid={`manual-csv-mapping-select-${header.normalizedHeader}`}
                    fullWidth
                    size="small"
                    value={currentValue}
                    SelectDisplayProps={{
                      "data-testid": `manual-csv-mapping-select-trigger-${header.normalizedHeader}`,
                    }}
                    onChange={(event) => onMappingChange(header.normalizedHeader, event.target.value)}
                  >
                    <MenuItem value="ignore">Ignore this column</MenuItem>
                    {fieldOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={selectedValues.includes(option.value)}
                      >
                        {renderOptionLabel(option)}
                      </MenuItem>
                    ))}
                  </MappingSelect>
                </MappingCell>

                <MappingCell>
                  <SampleText>
                    {isIgnored ? "-" : sampleValues[currentValue] || "-"}
                  </SampleText>
                </MappingCell>
              </MappingRow>
            );
          })}
        </MappingBody>
      </MappingTable>
    </div>
  );
}
